// `m` (not `motion`) — this component ships in the shared entry chunk, and
// full `motion` would drag every animation feature into the critical JS on
// every page. Features arrive via the LazyMotion in PersistentLayout (see
// lib/motion-features.ts).
import { m, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import { springBrand } from "@/lib/motion"
import { cn } from "@/lib/utils"

// Compact header logo: the favicon treatment (full-color mark on a Dark Lime
// tile) promoted into the header at phone widths, where the lockup crowds the
// bar. ViewBox, draw order, and fills mirror public/favicon.svg exactly so the
// header tile and the browser-tab icon read as the same object.
//
// Carries the "mark snap" signature motion — the logo assembling from its
// three parts (motion-guidelines §3). Same grammar and beats as the /brand
// showcase demo: chevron scales in, rhombus slides in along the diagonal,
// connector fades last; springBrand, staged 0/140/280ms. It plays once per
// FULL PAGE LOAD ("first paint" per the guidelines — so a reload replays it)
// but never on Inertia navigations, where the header remounts without a page
// load: a logo that reassembles on every page stops being a signature and
// starts being noise. The module-level flag below encodes exactly that split —
// it resets with the JS bundle (full load) and persists across client-side
// navigations.
//
// SSR and reduced motion both render assembled: `play` is false on the server
// and only flips after mount (via rAF — the eslint react-hooks rule forbids
// synchronous setState in an effect), so there's no hydration mismatch. The
// one-frame assembled flash before the snap is imperceptible at this size.

let snapPlayedThisLoad = false

export function SiteLogoMark({ className }: { className?: string }) {
  const reduceMotion = useReducedMotion()
  const [play, setPlay] = useState(false)
  // Survives StrictMode's dev-only effect double-run: the first run claims the
  // page-load flag, so the second run must not mistake its own claim for an
  // earlier navigation and bail. Fresh instances on later Inertia navigations
  // get a fresh ref and bail on the module flag as intended.
  const claimed = useRef(false)

  useEffect(() => {
    if (reduceMotion) return
    if (!claimed.current) {
      if (snapPlayedThisLoad) return
      snapPlayedThisLoad = true
      claimed.current = true
    }
    const raf = requestAnimationFrame(() => setPlay(true))
    return () => cancelAnimationFrame(raf)
  }, [reduceMotion])

  return (
    <span
      className={cn(
        // gb-tile--emblem: the CTA tile's material (gradient, border, insets)
        // without its button grammar — no ledge, no hover/press physics. Same
        // metal, different role: the button is the only thing that moves when
        // touched. The gradient keeps it legible on the dark header theme
        // too. size-8 matches the CTA's h-8 exactly.
        "gb-tile--emblem flex size-8 items-center justify-center",
        className,
      )}
    >
      {/* The viewBox is the favicon's crop (mark at ~63% of the tile); the
          extra p-0.5 eases the mark to ~55%, a touch more breathing room than
          the favicon since the tile sits next to text-bearing neighbors. */}
      <svg
        viewBox="100 100 800 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="size-full p-0.5"
      >
        {/* Connector — sits under the other pieces; fades in as the last beat. */}
        <m.path
          d="M519.62 753.55H449.778C390.49 753.55 353.881 688.858 384.407 638.033L467.538 499.619H537.38C596.668 499.619 633.278 564.311 602.752 615.137L519.62 753.55Z"
          fill="#04AE76"
          initial={false}
          animate={play ? { opacity: [0, 1] } : { opacity: 1 }}
          transition={play ? { ...springBrand, delay: 0.28 } : { duration: 0 }}
        />
        {/* Chevron — scales 0.85 → 1, the opening beat. */}
        <m.path
          d="M519.62 246.451H328.753L200.051 460.738C185.539 484.901 185.539 515.099 200.051 539.262L317.645 735.055C324.537 746.53 336.941 753.55 350.327 753.55H519.62L390.919 539.262C376.406 515.099 376.406 484.901 390.919 460.738L519.62 246.451Z"
          fill="url(#gb-header-mark-grad)"
          style={{ originX: "50%", originY: "50%", transformBox: "fill-box" }}
          initial={false}
          animate={
            play
              ? { scale: [0.85, 1], opacity: [0, 1] }
              : { scale: 1, opacity: 1 }
          }
          transition={play ? springBrand : { duration: 0 }}
        />
        {/* Rhombus — slides in along the diagonal from bottom-right. */}
        <m.path
          d="M620.049 753.55H810.688L669.285 518.114C662.393 506.639 649.988 499.619 636.602 499.619H467.538L620.049 753.55Z"
          fill="#06EF8E"
          initial={false}
          animate={
            play
              ? { x: [80, 0], y: [80, 0], opacity: [0, 1] }
              : { x: 0, y: 0, opacity: 1 }
          }
          transition={play ? { ...springBrand, delay: 0.14 } : { duration: 0 }}
        />
        <defs>
          <linearGradient
            id="gb-header-mark-grad"
            x1="348.046"
            y1="454.361"
            x2="347.87"
            y2="246.451"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#06EF8E" />
            <stop offset="1" stopColor="#05D186" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  )
}
